<script lang="ts">
  import { type IconValues } from '@gat-ship/icons'
  import { type ButtonProps } from '@gat-ship/vue-components/button'
  import { watch, type HTMLAttributes } from 'vue'
  import { useButtonDropdown } from '../button-dropdown/button-dropdown.vue'
  import { useModalContext } from '@gat-ship/vue-components'
  import { useContentToggle } from '../../content-components/content-toggle.vue'
  import { BtnAiGradient } from '@gat-ship/vue-components'

  export interface GenericButtonProps {
    class?: HTMLAttributes['class']
    label?: string
    icon?: IconValues | false
    disabled?: boolean
    variant?: ButtonProps['variant'] | 'gradient'
    size?: ButtonProps['size']
    loading?: boolean
    loadingId?: string | string[]
    testId?: string
    keepOpenOnClick?: boolean
  }
</script>

<script setup lang="ts">
  import { DropdownMenuItem } from '@gat-ship/vue-components/dropdown-menu'
  import { useGenericButton } from './generic-button-provider.vue'

  defineOptions({
    inheritAttrs: false
  })

  const { isOfParentType } = useGenericButton()
  const dropdownContext = useButtonDropdown()
  const modalContext = useModalContext()
  const contentToggleContext = useContentToggle()
  const props = defineProps<GenericButtonProps>()

  watch(modalContext.isOpen, (modalOpenState) => {
    if (modalOpenState === false) {
      // Reset the dropdown context if the modal is closed
      dropdownContext.open.value = false
      dropdownContext.keepOpenOnClick.value = false
    }
  })

  const emit = defineEmits<{
    (e: 'click', event?: MouseEvent): void
  }>()

  function handleClick(event?: MouseEvent) {
    if (contentToggleContext.provided) {
      contentToggleContext.open.value = true
      return
    }

    if (props.keepOpenOnClick) {
      dropdownContext.keepOpenOnClick.value = true
    }
    emit('click', event)
  }

  defineExpose({
    closeDropdown() {
      dropdownContext.open.value = false
      dropdownContext.keepOpenOnClick.value = false
    },
    openDropdown(keepOpen?: boolean) {
      dropdownContext.keepOpenOnClick.value = keepOpen ?? false
      dropdownContext.open.value = true
    }
  })
</script>

<template>
  <template v-if="isOfParentType === 'none'">
    <btn-ai-gradient
      v-if="variant === 'gradient'"
      v-bind="props"
      :icon="{ icon: 'tabler:sparkles-2' }"
      @click="handleClick"
    />
    <gat-button
      v-else
      :class
      :label
      :testId
      :disabled
      :loading
      :loading-id
      :variant
      :size
      :icon="icon && !$slots.default ? icon : undefined"
      @click="handleClick"
    >
      <slot v-if="$slots.default">
        <gat-icon v-if="icon && !props.loading" class="size-4" :icon="icon" />
        {{ label }}
      </slot>
    </gat-button>
  </template>
  <DropdownMenuItem
    v-else-if="isOfParentType === 'dropdownMenu'"
    :class
    :data-testid="testId"
    :disabled="disabled"
    :loading="loading"
    @click="handleClick"
  >
    <slot>
      <gat-icon v-if="icon && !props.loading" class="size-4" :icon="icon" />
      {{ label }}
    </slot>
  </DropdownMenuItem>
</template>
